/**
 * Global arama indeksi — header'daki site içi arama (komut paleti) için.
 *
 * İki tür kayıt vardır:
 *  - Sayfalar: ROUTE_COMPONENT_MAP'ten otomatik üretilir (parametreli rotalar hariç).
 *  - Hızlı eylemler: "yeni fatura", "yeni kasa ekle" gibi doğrudan bir forma/işleme
 *    götüren kısayollar (ACTION_ENTRIES).
 *
 * Arama Türkçe karakter toleranslıdır (ş→s, ı→i, ö→o…), böylece "irsaliye"
 * yazan da "İrsaliye" kaydını bulur.
 */
import { ROUTE_COMPONENT_MAP } from './routeComponentMap';

export interface SearchEntry {
  id: string;
  path: string;
  /** Türkçe başlık */
  title: string;
  /** İngilizce başlık */
  titleEn: string;
  /** Eşleşmeye katılan ek anahtar kelimeler (TR + EN karışık yazılabilir) */
  keywords: string[];
  /** true = hızlı eylem (dropdown'da üstte, farklı rozetle gösterilir) */
  isAction: boolean;
}

/** Türkçe duyarlı normalize: küçük harf + aksan/karakter sadeleştirme. */
export const normalizeSearchText = (s: string): string =>
  (s || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/i̇/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ğ/g, 'g')
    .replace(/â/g, 'a')
    .replace(/î/g, 'i')
    .replace(/û/g, 'u')
    .trim();

/** Hızlı eylemler — form açan / doğrudan işleme götüren kısayollar. */
const ACTION_ENTRIES: SearchEntry[] = [
  {
    id: 'act-new-sales-invoice',
    path: '/fatura/satis/yeni',
    title: 'Yeni Satış Faturası',
    titleEn: 'New Sales Invoice',
    keywords: ['yeni fatura', 'fatura kes', 'fatura olustur', 'satis faturasi ekle', 'invoice create'],
    isAction: true,
  },
  {
    id: 'act-new-purchase-invoice',
    path: '/fatura/alis/yeni',
    title: 'Yeni Alış Faturası',
    titleEn: 'New Purchase Invoice',
    keywords: ['yeni fatura', 'alis faturasi ekle', 'fatura gir', 'purchase invoice create'],
    isAction: true,
  },
  {
    id: 'act-new-sales-offer',
    path: '/teklifler/satis/yeni',
    title: 'Yeni Satış Teklifi',
    titleEn: 'New Sales Offer',
    keywords: ['yeni teklif', 'teklif olustur', 'teklif ver', 'offer create'],
    isAction: true,
  },
  {
    id: 'act-new-purchase-offer',
    path: '/teklifler/alis/yeni',
    title: 'Yeni Alış Teklifi',
    titleEn: 'New Purchase Offer',
    keywords: ['yeni teklif', 'alinan teklif', 'teklif al', 'offer create'],
    isAction: true,
  },
  {
    id: 'act-new-sales-order',
    path: '/siparis/satis/yeni',
    title: 'Yeni Satış Siparişi',
    titleEn: 'New Sales Order',
    keywords: ['yeni siparis', 'siparis olustur', 'siparis ekle', 'order create'],
    isAction: true,
  },
  {
    id: 'act-new-purchase-order',
    path: '/siparis/alis/yeni',
    title: 'Yeni Alış Siparişi',
    titleEn: 'New Purchase Order',
    keywords: ['yeni siparis', 'alis siparisi', 'satin alma siparisi', 'purchase order create'],
    isAction: true,
  },
  {
    id: 'act-new-waybill',
    path: '/irsaliye/yeni',
    title: 'Yeni İrsaliye',
    titleEn: 'New Waybill',
    keywords: ['yeni irsaliye', 'irsaliye olustur', 'irsaliye kes', 'sevk irsaliyesi', 'waybill create'],
    isAction: true,
  },
  {
    id: 'act-cashbox-manage',
    path: '/kasa',
    title: 'Kasa Yönetimi / Yeni Kasa',
    titleEn: 'Cashbox Management / New Cashbox',
    keywords: ['yeni kasa', 'kasa ekle', 'kasa ekleme', 'kasa olustur', 'kasa yonetimi', 'cashbox add'],
    isAction: true,
  },
  {
    id: 'act-bank-manage',
    path: '/banka',
    title: 'Banka Yönetimi / Yeni Banka Hesabı',
    titleEn: 'Bank Management / New Bank Account',
    keywords: ['yeni banka', 'banka hesabi ekle', 'hesap ekle', 'iban', 'bank account add'],
    isAction: true,
  },
  {
    id: 'act-account-manage',
    path: '/cari',
    title: 'Cari Hesaplar / Yeni Cari',
    titleEn: 'Accounts / New Account',
    keywords: ['yeni cari', 'cari ekle', 'musteri ekle', 'tedarikci ekle', 'customer add'],
    isAction: true,
  },
  {
    id: 'act-product-manage',
    path: '/stok',
    title: 'Stok / Yeni Ürün',
    titleEn: 'Inventory / New Product',
    keywords: ['yeni urun', 'urun ekle', 'stok ekle', 'stok girisi', 'product add'],
    isAction: true,
  },
  {
    id: 'act-checknote-manage',
    path: '/cek-senet',
    title: 'Çek/Senet / Yeni Çek-Senet',
    titleEn: 'Checks & Notes / New Check-Note',
    keywords: ['yeni cek', 'cek ekle', 'senet ekle', 'yeni senet', 'check add', 'promissory note'],
    isAction: true,
  },
  {
    id: 'act-warehouse-manage',
    path: '/depo',
    title: 'Depo Yönetimi / Yeni Depo',
    titleEn: 'Warehouse Management / New Warehouse',
    keywords: ['yeni depo', 'depo ekle', 'warehouse add'],
    isAction: true,
  },
  {
    id: 'act-project-manage',
    path: '/projeler',
    title: 'Projeler / Yeni Proje',
    titleEn: 'Projects / New Project',
    keywords: ['yeni proje', 'proje ekle', 'proje olustur', 'project add'],
    isAction: true,
  },
];

/**
 * Sayfa bazlı ek anahtar kelimeler (eş anlamlılar). ROUTE_COMPONENT_MAP
 * başlıkları zaten indekste; buradakiler kullanıcıların farklı söyleyişleri.
 */
const PAGE_EXTRA_KEYWORDS: Record<string, string[]> = {
  '/cari': ['musteri', 'tedarikci', 'cari hesap', 'customers'],
  '/stok': ['urun', 'envanter', 'inventory', 'stok listesi'],
  '/kasa': ['nakit', 'kasa yonetimi', 'cash'],
  '/banka': ['hesap', 'iban', 'bank'],
  '/doviz': ['kur', 'dolar', 'euro', 'exchange rate', 'doviz kurlari'],
  '/fatura/satis': ['satis faturalari', 'kesilen faturalar'],
  '/fatura/alis': ['alis faturalari', 'gelen faturalar'],
  '/e-fatura': ['gib', 'earsiv', 'e arsiv', 'gelen e fatura'],
  '/teklifler': ['fiyat teklifi', 'proforma'],
  '/irsaliyeler': ['sevk', 'sevkiyat'],
  '/siparisler': ['satis siparisi', 'alis siparisi'],
  '/cek-senet': ['cek', 'senet', 'vadeli'],
  '/projeler': ['proje', 'proje yonetimi', 'proje karliligi', 'proje maliyeti', 'santiye', 'project'],
  '/krediler': ['kredi', 'taksit'],
  '/kredi-odemeleri': ['taksit odeme'],
  '/hizli-satis': ['pos', 'satis ekrani', 'market satis'],
  '/restoran': ['masa', 'adisyon'],
  '/gun-sonu': ['z raporu', 'gun sonu raporu'],
  '/personnel': ['calisan', 'personel listesi'],
  '/accounting/dashboard': ['yonetici', 'ozet', 'panel', 'muhasebe paneli'],
  '/depo': ['warehouse', 'depo listesi'],
  '/entegrasyonlar': ['trendyol', 'yemeksepeti', 'getir', 'migros', 'entegrasyon'],
  '/qr-menu-management': ['qr', 'menu'],
  '/stok-hareket-raporu': ['stok hareketleri'],
  '/cari-hareket-raporu': ['cari hareketleri', 'ekstre'],
};

/**
 * İndeks LAZY oluşturulur: routeComponentMap tüm sayfa bileşenlerini import
 * ettiği için (ve o sayfalar dolaylı olarak Header'a geri döndüğü için)
 * modül yüklenirken ROUTE_COMPONENT_MAP'e erişmek döngüsel import hatası
 * verir ("Cannot access 'ROUTE_COMPONENT_MAP' before initialization").
 * İlk aramada oluşturup cache'liyoruz; o anda tüm modüller hazır olur.
 */
let cachedEntries: SearchEntry[] | null = null;

export const getSearchEntries = (): SearchEntry[] => {
  if (!cachedEntries) {
    const pageEntries: SearchEntry[] = Object.entries(ROUTE_COMPONENT_MAP)
      .filter(([path]) => !path.includes(':'))
      .map(([path, entry]) => ({
        id: `page:${path}`,
        path,
        title: entry.title,
        titleEn: entry.titleEn || entry.title,
        keywords: PAGE_EXTRA_KEYWORDS[path] || [],
        isAction: false,
      }));
    cachedEntries = [...ACTION_ENTRIES, ...pageEntries];
  }
  return cachedEntries;
};

export interface SearchResult {
  entry: SearchEntry;
  score: number;
}

/**
 * Arama: her sorgu kelimesi, kaydın normalize alanlarından en az birinde
 * geçmelidir (AND). Skor: başlık tam eşleşme > başlık başlangıcı > başlıkta
 * geçme > anahtar kelime. Eylemler eşit skorda öne alınır.
 */
export const searchEntries = (query: string, limit = 10): SearchResult[] => {
  const q = normalizeSearchText(query);
  if (!q) return [];
  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];

  const results: SearchResult[] = [];

  for (const entry of getSearchEntries()) {
    const nTitle = normalizeSearchText(entry.title);
    const nTitleEn = normalizeSearchText(entry.titleEn);
    const nKeywords = entry.keywords.map(normalizeSearchText);

    let score = 0;
    let allMatched = true;

    for (const token of tokens) {
      let tokenScore = 0;
      if (nTitle === token || nTitleEn === token) tokenScore = 100;
      else if (nTitle.startsWith(token) || nTitleEn.startsWith(token)) tokenScore = 60;
      else if (
        nTitle.split(/\s+|\//).some((w) => w.startsWith(token)) ||
        nTitleEn.split(/\s+|\//).some((w) => w.startsWith(token))
      ) tokenScore = 45;
      else if (nTitle.includes(token) || nTitleEn.includes(token)) tokenScore = 30;
      else if (nKeywords.some((k) => k === token)) tokenScore = 40;
      else if (nKeywords.some((k) => k.split(/\s+/).some((w) => w.startsWith(token)))) tokenScore = 25;
      else if (nKeywords.some((k) => k.includes(token))) tokenScore = 15;

      if (tokenScore === 0) {
        allMatched = false;
        break;
      }
      score += tokenScore;
    }

    if (allMatched) {
      if (entry.isAction) score += 5;
      results.push({ entry, score });
    }
  }

  results.sort((a, b) => b.score - a.score);
  return results.slice(0, limit);
};
